import type { HarmonyPackageBoard } from '@rhythmic/db/types'
import type { Transaction } from '@tanstack/vue-db'
import { createOptimisticAction } from '@tanstack/vue-db'
import { getBoardsCollection } from '../getCollections'

export interface UpdateHarmonyPlanBoardPayload {
  name?: string
  image?: string | null
  context?: Record<string, unknown> | string | null
  iterationId?: string | null
  publishStatus?: string | null
  forecastStartDate?: string | null
  forecastEndDate?: string | null
  forecastBurndown?: number[] | null
  costPerIteration?: number | string | null
  integrationsMap?: Record<string, unknown>[] | null
  folderId?: string | null
}

interface UpdateContext {
  planId: string
}

function validateParams(boardId: string, payload: UpdateHarmonyPlanBoardPayload, context: UpdateContext) {
  if (!boardId) throw new Error('Board identifier is required to update a harmony plan board')

  if (!context.planId) throw new Error('Harmony plan identifier is required to update a harmony plan board')

  if (
    payload.name === undefined &&
    payload.image === undefined &&
    payload.context === undefined &&
    payload.iterationId === undefined &&
    payload.publishStatus === undefined &&
    payload.forecastStartDate === undefined &&
    payload.forecastEndDate === undefined &&
    payload.forecastBurndown === undefined &&
    payload.costPerIteration === undefined &&
    payload.integrationsMap === undefined &&
    payload.folderId === undefined
  ) {
    throw new Error('At least one field is required to update a harmony plan board')
  }

  return { boardId, planId: context.planId }
}

export function handleUpdateHarmonyPlanBoard(
  boardId: string,
  payload: UpdateHarmonyPlanBoardPayload,
  context: UpdateContext,
): Transaction {
  const { planId } = validateParams(boardId, payload, context)
  const collection = getBoardsCollection(planId)

  const updateTx = createOptimisticAction<{
    boardId: string
    payload: UpdateHarmonyPlanBoardPayload
  }>({
    onMutate: (mutation) => {
      collection.update(mutation.boardId, (draft: HarmonyPackageBoard) => {
        if (mutation.payload.name !== undefined) draft.name = mutation.payload.name
        if (mutation.payload.image !== undefined) draft.image = mutation.payload.image
        if (mutation.payload.context !== undefined) draft.context = mutation.payload.context as any
        if (mutation.payload.iterationId !== undefined) draft.iteration_id = mutation.payload.iterationId
        if (mutation.payload.publishStatus !== undefined) draft.publish_status = mutation.payload.publishStatus
        if (mutation.payload.forecastStartDate !== undefined)
          draft.forecast_start_date = mutation.payload.forecastStartDate as any
        if (mutation.payload.forecastEndDate !== undefined)
          draft.forecast_end_date = mutation.payload.forecastEndDate as any
        if (mutation.payload.forecastBurndown !== undefined)
          draft.forecast_burndown = mutation.payload.forecastBurndown as any
        if (mutation.payload.costPerIteration !== undefined)
          draft.cost_per_iteration = mutation.payload.costPerIteration as any
        if (mutation.payload.integrationsMap !== undefined)
          draft.integrations_map = mutation.payload.integrationsMap as any
        if (mutation.payload.folderId !== undefined) draft.folder_id = mutation.payload.folderId
        draft.updated_at = new Date()
      })
    },
    mutationFn: async (mutation) => {
      const body: Record<string, unknown> = {}
      if (mutation.payload.name !== undefined) body.name = mutation.payload.name
      if (mutation.payload.image !== undefined) body.image = mutation.payload.image
      if (mutation.payload.context !== undefined) body.context = mutation.payload.context
      if (mutation.payload.iterationId !== undefined) body.iteration_id = mutation.payload.iterationId
      if (mutation.payload.publishStatus !== undefined) body.publish_status = mutation.payload.publishStatus
      if (mutation.payload.forecastStartDate !== undefined)
        body.forecast_start_date = mutation.payload.forecastStartDate
      if (mutation.payload.forecastEndDate !== undefined) body.forecast_end_date = mutation.payload.forecastEndDate
      if (mutation.payload.forecastBurndown !== undefined) body.forecast_burndown = mutation.payload.forecastBurndown
      if (mutation.payload.costPerIteration !== undefined) body.cost_per_iteration = mutation.payload.costPerIteration
      if (mutation.payload.integrationsMap !== undefined) body.integrations_map = mutation.payload.integrationsMap
      if (mutation.payload.folderId !== undefined) body.folder_id = mutation.payload.folderId

      const result = await $fetch<{ txid?: string | number }>(`/api/harmony-package-boards/${mutation.boardId}`, {
        method: 'PUT',
        body,
      })

      if (result?.txid && collection.utils.awaitTxId) {
        await collection.utils.awaitTxId(Number.parseInt(result.txid.toString()))
      }
    },
  })

  return updateTx({ boardId, payload })
}
